import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { collectTracking } from "@/lib/utm";
import { TRIAGE_QUESTIONS, MAJOR_TRIGGER_LABELS } from "@/lib/constants";
import LeadFields, { Field } from "@/components/LeadFields";
import { Input } from "@/components/ui/input";
import { AlertTriangle, ShieldCheck, ShieldAlert, ArrowRight } from "lucide-react";

const CHOICES = ["Yes", "No", "Not Sure"];
const CONDITIONAL_CHOICES = ["Yes", "No / Not known"];

export default function Triage() {
  const [values, setValues] = useState({
    first_name: "", last_name: "", email: "", mobile: "", company: "",
    buyer_type: "", site_postcode_or_location: "", consent: false,
  });
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState("intake"); // intake | questions | result
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const startTriage = (e) => {
    e.preventDefault();
    if (!values.consent) return toast.error("Please tick the consent box to continue.");
    if (!values.site_postcode_or_location.trim())
      return toast.error("Site postcode or location is required.");
    setStep("questions");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setAnswer = (id, choice) => setAnswers((s) => {
    const next = { ...s, [id]: choice };
    const q = TRIAGE_QUESTIONS.find((x) => x.id === id);
    if (q?.conditional && choice === "No") delete next[q.conditional.key];
    return next;
  });

  const allAnswered = TRIAGE_QUESTIONS.every((q) => !!answers[q.id]);
  const answeredCount = TRIAGE_QUESTIONS.filter((q) => !!answers[q.id]).length;

  const submit = async () => {
    if (!allAnswered) return toast.error("Please answer all 12 questions.");
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        triage_answers: answers,
        tracking: collectTracking({ formType: "triage", enquiryType: "pfas_src_triage_tool" }),
      };
      const { data } = await api.post("/leads/triage", payload);
      setResult(data);
      setStep("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-16">
      <div className="kicker mb-4">Free · No obligation · 3 minutes</div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">Free PFAS Risk Triage Tool</h1>
      <p className="mt-5 text-white/65 max-w-2xl">
        Answer 12 practical questions for an evidence-weighted preliminary view of whether your site may justify a PFAS-specific desk-based review.
      </p>

      <div className="mt-10 border border-white/10 bg-[#0C0E13] p-6 md:p-10">
        {step === "intake" && (
          <form onSubmit={startTriage} className="space-y-8">
            <LeadFields values={values} onChange={setField}
              consentLabel="I agree to receive my triage result and relevant PFAS / brownfield risk information from PFAS Risk Intelligence."
              idPrefix="triage" />
            <Field label="Site postcode or location" required>
              <Input
                value={values.site_postcode_or_location}
                onChange={(e) => setField("site_postcode_or_location", e.target.value)}
                data-testid="triage-site-postcode"
                required
                placeholder="e.g. WA1 4EL or 'Land north of Ashton Rd'"
                className="bg-[#12141A] border-white/10 rounded-none h-11"
              />
            </Field>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button
                type="submit"
                data-testid="triage-start-btn"
                className="inline-flex items-center gap-2 bg-white text-[#0A0B0E] px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:bg-white/90"
              >
                Start the 12 Questions <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </button>
              <span className="text-xs text-white/40">Your details are only used to deliver your result and relevant risk intelligence.</span>
            </div>
          </form>
        )}

        {step === "questions" && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div className="kicker">Step 2 / 3 · 12 questions</div>
              <div className="text-[11px] font-mono-alt tracking-[0.18em] uppercase text-white/50">
                {answeredCount} / 12 answered
              </div>
            </div>
            <div className="w-full h-1 bg-white/[0.08]">
              <div className="h-1 bg-emerald-500 transition-all" style={{ width: `${(answeredCount / 12) * 100}%` }} />
            </div>
            <p className="text-xs text-white/45 leading-relaxed">
              Evidence-weighted: PFAS-specific indicators (AFFF, fire-training, airfield firefighting, known PFAS investigations, PFAS-relevant processes, clear pathways with a plausible source) carry more weight than generic brownfield history.
            </p>
            <div className="space-y-6">
              {TRIAGE_QUESTIONS.map((q, i) => (
                <div key={q.id} className="border border-white/10 p-5 md:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[11px] font-mono-alt tracking-[0.18em] uppercase text-white/40">
                        Q{i + 1}
                        {q.major && <span className="ml-2 text-red-400/80">· Major trigger</span>}
                        {q.conditional && <span className="ml-2 text-amber-400/80">· Major only if PFAS-specific</span>}
                        {!q.major && !q.conditional && <span className="ml-2 text-white/30">· Contextual indicator</span>}
                      </div>
                      <div className="mt-2 font-display text-lg leading-snug">{q.q}</div>
                      {q.examples && <p className="mt-2 text-sm text-white/50">Examples: {q.examples}</p>}
                      <p className="mt-2 text-xs text-white/40 italic">{q.impact}</p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {CHOICES.map((c) => {
                      const active = answers[q.id] === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setAnswer(q.id, c)}
                          data-testid={`triage-${q.id}-${c.replace(/\s+/g,'').toLowerCase()}`}
                          className={`py-2.5 text-[12px] font-mono-alt tracking-[0.14em] uppercase border transition-colors ${
                            active
                              ? "bg-white text-[#0A0B0E] border-white"
                              : "border-white/15 text-white/70 hover:border-white/40"
                          }`}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                  {q.conditional && (answers[q.id] === "Yes" || answers[q.id] === "Not Sure") && (
                    <div className="mt-4 border border-amber-500/30 bg-amber-500/[0.05] p-4" data-testid={`triage-${q.id}-followup`}>
                      <div className="text-[10px] font-mono-alt tracking-[0.18em] uppercase text-amber-300">Follow-up · PFAS-specific evidence</div>
                      <div className="mt-2 text-sm text-white/85">{q.conditional.label}</div>
                      <p className="mt-1 text-xs text-white/45">{q.conditional.hint}</p>
                      <div className="mt-3 grid grid-cols-2 gap-2 max-w-sm">
                        {CONDITIONAL_CHOICES.map((c) => {
                          const stored = answers[q.conditional.key];
                          const active = c === "Yes" ? stored === "Yes" : stored === "No";
                          return (
                            <button key={c} type="button"
                              onClick={() => setAnswers((s) => ({ ...s, [q.conditional.key]: c === "Yes" ? "Yes" : "No" }))}
                              data-testid={`triage-${q.conditional.key}-${c === "Yes" ? "yes" : "no"}`}
                              className={`py-2 text-[11px] font-mono-alt tracking-[0.14em] uppercase border transition-colors ${
                                active ? "bg-amber-400 text-[#0A0B0E] border-amber-400" : "border-white/15 text-white/70 hover:border-white/40"}`}>
                              {c}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4 pt-4">
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !allAnswered}
                data-testid="triage-submit-btn"
                className="inline-flex items-center gap-2 bg-white text-[#0A0B0E] px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? "Scoring…" : "See My Result"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </button>
              <span className="text-xs text-white/40">Your answers are stored securely.</span>
            </div>
          </div>
        )}

        {step === "result" && result && <TriageResult result={result} />}
      </div>

      <p className="mt-6 text-xs text-white/40 leading-relaxed">
        This preliminary desk-based result does not confirm the presence or absence of PFAS or other contamination. It is a screening prompt, not an assessment.
      </p>
    </div>
  );
}

function TriageResult({ result }) {
  const level = result.triage_result; // Low | Amber | Red
  const cfg = {
    Low: {
      Icon: ShieldCheck,
      title: "Low Initial Concern",
      badgeCls: "bg-emerald-500/10 border-emerald-500/40 text-emerald-400",
      body: "Based on your answers, no PFAS-specific major trigger has been identified and the overall count of contextual indicators is low. This is not a clean bill of health: keep the checklist on file and revisit the position if site information, planning context, lender, buyer or insurer questions change.",
      primaryCta: { text: "Download the Free PFAS Risk Checklist", to: "/checklist" },
      secondaryCta: { text: "Subscribe to PFAS Risk Watch", to: "/risk-watch" },
    },
    Amber: {
      Icon: AlertTriangle,
      title: "Amber · Further Review Justified",
      badgeCls: "bg-amber-500/10 border-amber-500/40 text-amber-400",
      body: "Your answers indicate either one PFAS-specific major trigger or enough contextual indicators and evidence gaps to justify an early desk-based PFAS review before planning, acquisition, funding, insurance or transaction decisions progress further. A PFAS Brownfield Risk Screen can help identify potential sources, pathways, evidence gaps, commercial flags and proportionate next steps.",
      primaryCta: { text: "Request Risk Screen · £1,950+ VAT", to: "/risk-screen" },
      secondaryCta: { text: "Download the Free Checklist", to: "/checklist" },
    },
    Red: {
      Icon: ShieldAlert,
      title: "Red · Early Review Strongly Recommended",
      badgeCls: "bg-red-500/10 border-red-500/40 text-red-400",
      body: "Your answers indicate multiple PFAS-specific major triggers, or a high overall count of indicators, suggesting PFAS uncertainty could materially affect planning, cost, funding, insurance, transaction or strategy. Early PFAS-specific review is strongly recommended before committing further capital or allowing the issue to surface late.",
      primaryCta: { text: "Request Risk Screen · £1,950+ VAT", to: "/risk-screen" },
      secondaryCta: { text: "Download the Free Checklist", to: "/checklist" },
    },
  }[level] || {};
  const { Icon, title, badgeCls, body, primaryCta, secondaryCta } = cfg;
  const hasPathway = result.major_triggers?.includes("q5");

  return (
    <div data-testid="triage-result" className="space-y-8">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="kicker mb-3">Preliminary Triage Result</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight" data-testid="triage-result-title">{title}</h2>
        </div>
        <div className={`inline-flex items-center gap-3 border ${badgeCls} px-4 py-3`}>
          {Icon && <Icon className="w-5 h-5" strokeWidth={2} />}
          <div>
            <div className="text-[10px] font-mono-alt tracking-[0.18em] uppercase opacity-80">Risk Level</div>
            <div className="font-display text-xl leading-none mt-1" data-testid="triage-result-level">{level.toUpperCase()}</div>
          </div>
        </div>
      </div>
      <p className="text-white/70 leading-relaxed max-w-3xl">{body}</p>
      <p className="text-sm text-white border-l-2 border-white/30 pl-4 max-w-3xl" data-testid="triage-result-caveat">
        This preliminary desk-based result does not confirm the presence or absence of PFAS.
      </p>
      {result.major_triggers?.length > 0 && (
        <div>
          <div className="kicker mb-3">Major triggers identified</div>
          <div className="flex flex-wrap gap-2">
            {result.major_triggers.map((q) => (
              <span key={q} className="border border-red-500/30 bg-red-500/10 text-red-300 px-3 py-1.5 text-[11px] font-mono-alt tracking-[0.14em] uppercase">
                {MAJOR_TRIGGER_LABELS[q] || q}
              </span>
            ))}
          </div>
          {hasPathway && (
            <p className="mt-3 text-xs text-white/50 max-w-3xl leading-relaxed">
              Pathways matter in combination with a plausible source and a receptor. The presence of groundwater or surface water alone does not establish elevated PFAS risk; it indicates a route by which any potential source could reach the site or a receptor.
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-3 pt-2">
        <Link to={primaryCta.to} data-testid="triage-cta-primary"
          className="inline-flex items-center gap-2 bg-white text-[#0A0B0E] px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:bg-white/90">
          {primaryCta.text} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
        </Link>
        <Link to={secondaryCta.to} data-testid="triage-cta-secondary"
          className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:border-white/60">
          {secondaryCta.text}
        </Link>
      </div>
      <div className="text-xs text-white/40 border-t border-white/[0.06] pt-6">
        Reference: <span className="font-mono-alt tracking-[0.12em]">{result.submission_id}</span> · Score {result.triage_score} / 12 · Major triggers {result.major_triggers?.length || 0}
        <span className="block mt-2">Thresholds: Red = 2+ major triggers or score 7+ · Amber = 1 major trigger or score 4–6 · Low Initial Concern = no major trigger and score 0–3.</span>
      </div>
    </div>
  );
}
